import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import type { MealInsightHistoryResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import {
  useIntersectionObserver,
  useMediaQuery
} from "@react-hooks-library/core";
import { APP_BREAKPOINT } from "src/constants/layout";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { useBloc } from "src/state/state";
import type { MealDayGroup } from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import Translate from "src/ui/components/Translate/Translate";
import type { TranslationKey } from "src/types/translationKey";

import MealCard from "./MealCard";
import MealInsightSkeleton from "./MealInsightSkeleton";

/**
 * Distance below the viewport at which the bottom sentinel counts as "in view",
 * expressed in viewport heights so it scales per device. Loading ~2 screens
 * ahead keeps the next page ready well before the member reaches it, so paging
 * stays smooth instead of catching up to the end of the list.
 */
const PREFETCH_MARGIN = "200%";

const List = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const DayBar = styled.p`
  width: 100%;
  margin: 0;
  padding: 0.5rem 1.5rem 0.5rem 0.5rem;
  text-align: left;
  color: var(--color-charcoal, #212121);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.32px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DaySection = styled.section`
  display: flex;
  flex-direction: column;
  padding-bottom: 1.5rem;
`;

const DayHeader = styled.h2`
  margin: 0;
  padding: 0.5rem 0 0.25rem 0.25rem;
  color: var(--color-charcoal-60);
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DayMeals = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 0;
`;

const LoadingMore = styled.div`
  padding-top: 1rem;
`;

const CaughtUp = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 0;
  color: color-mix(in srgb, var(--color-charcoal, #212121) 80%, transparent);
  font-size: 0.875rem;
  line-height: 1.43;
  letter-spacing: -0.28px;
`;

/**
 * Off-screen live region. Paging happens silently on scroll —
 * announcing when the next page is loading and when the member reaches the end.
 */
const LiveStatus = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const LOAD_MORE_SKELETON_COUNT = 2;

interface MealInsightDaySectionProps {
  group: MealDayGroup;
  registerSection: (dateKey: string, el: HTMLElement | null) => void;
  onMealClick: (meal: MealInsightHistoryResponse) => void;
}

const MealInsightDaySection: FC<MealInsightDaySectionProps> = ({
  group,
  registerSection,
  onMealClick
}) => (
  <DaySection ref={(el) => registerSection(group.dateKey, el)}>
    <DayHeader>{group.label}</DayHeader>
    <DayMeals>
      {group.meals.map((meal) => (
        <MealCard key={meal.id} meal={meal} onClick={onMealClick} />
      ))}
    </DayMeals>
  </DaySection>
);

interface MealInsightDayListProps {
  onMealClick: (meal: MealInsightHistoryResponse) => void;
}

const MealInsightDayList: FC<MealInsightDayListProps> = ({ onMealClick }) => {
  const [{ dayGroups, hasMore, loadingMore }, { loadMore }] =
    useBlocNext(MealInsightsCubit);
  const [, { setRenderPortal }] = useBloc(StatusBarBloc);
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);

  const listRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const [activeDateKey, setActiveDateKey] = useState<string | null>(null);

  const prefetchRef = useRef<HTMLDivElement>(null);
  // Observe against the real scroll container (Ionic's inner-scroll), not the
  // viewport — otherwise the intermediate scroller clips the sentinel and the
  // rootMargin can't prefetch, firing only once the member hits the very end.
  const { inView } = useIntersectionObserver(prefetchRef, {
    root: scrollEl ?? undefined,
    rootMargin: `0px 0px ${PREFETCH_MARGIN} 0px`
  });
  // No margin: only true once the member has actually reached the end. Combined
  // with loadingMore this surfaces the skeleton only when the network hasn't
  // kept up — never during the normal, already-prefetched case.
  const { inView: reachedEnd } = useIntersectionObserver(prefetchRef, {
    root: scrollEl ?? undefined
  });

  const activeLabel = useMemo(
    () => dayGroups.find((group) => group.dateKey === activeDateKey)?.label,
    [dayGroups, activeDateKey]
  );

  const registerSection = useCallback(
    (dateKey: string, el: HTMLElement | null): void => {
      if (el) {
        sectionRefs.current.set(dateKey, el);
      } else {
        sectionRefs.current.delete(dateKey);
      }
    },
    []
  );

  /**
   * The day bar shows the section that currently straddles the top of the
   * scroll area. Driving one active key off scroll geometry (rather than a
   * per-header sticky toggle) keeps a single persistent bar that only swaps its
   * label when the day changes, and stays null only before the first scroll.
   */
  const updateActiveDay = useCallback((): void => {
    const pinY = scrollEl ? scrollEl.getBoundingClientRect().top : 0;
    let active: string | null = null;

    sectionRefs.current.forEach((el, dateKey) => {
      const rect = el.getBoundingClientRect();
      if (rect.top - pinY <= 1 && rect.bottom - pinY > 1) {
        active = dateKey;
      }
    });

    setActiveDateKey((prev) => (prev === active ? prev : active));
  }, [scrollEl]);

  useEffect(() => {
    const ionContent = listRef.current?.closest("ion-content") as
      | (HTMLElement & { getScrollElement?: () => Promise<HTMLElement> })
      | null;
    if (!ionContent) {
      return;
    }
    // Prefer the inner-scroll element directly: getScrollElement() only resolves
    // a tick later, and on iOS the interim window fallback never fires (the
    // ion-content scrolls its own shadow element, not the window) — so the
    // active day is never computed.
    const innerScroll =
      ionContent.shadowRoot?.querySelector<HTMLElement>(".inner-scroll");
    if (innerScroll) {
      setScrollEl(innerScroll);
      return;
    }
    if (!ionContent.getScrollElement) {
      return;
    }
    let cancelled = false;
    void ionContent.getScrollElement().then((el) => {
      if (!cancelled) {
        setScrollEl(el);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const targets: (HTMLElement | Window)[] = scrollEl
      ? [scrollEl, window]
      : [window];
    targets.forEach((target) =>
      target.addEventListener("scroll", updateActiveDay, { passive: true })
    );
    updateActiveDay();
    return () =>
      targets.forEach((target) =>
        target.removeEventListener("scroll", updateActiveDay)
      );
  }, [scrollEl, updateActiveDay]);

  useEffect(() => {
    updateActiveDay();
  }, [dayGroups, updateActiveDay]);

  useEffect(() => {
    if (inView && hasMore && !loadingMore) {
      void loadMore();
    }
  }, [inView, hasMore, loadingMore, loadMore]);

  const showDayBar = Boolean(activeLabel) && !isDesktop;

  useEffect(() => {
    setRenderPortal(showDayBar);
  }, [showDayBar, setRenderPortal]);

  useEffect(() => {
    return () => setRenderPortal(false);
  }, [setRenderPortal]);

  const dayStatusBarPortal =
    typeof document === "undefined"
      ? null
      : document.getElementById("statusBarPortalTarget");

  const reachedCaughtUp = !hasMore && dayGroups.length > 0;
  const liveStatusKey: TranslationKey | null = loadingMore
    ? "mealInsights.meals.loadingMore"
    : reachedCaughtUp
      ? "mealInsights.meals.allCaughtUp"
      : null;

  return (
    <List ref={listRef}>
      <LiveStatus role="status" aria-live="polite">
        {liveStatusKey && <Translate msg={liveStatusKey} />}
      </LiveStatus>

      {showDayBar &&
        dayStatusBarPortal &&
        ReactDOM.createPortal(
          <DayBar aria-hidden>{activeLabel}</DayBar>,
          dayStatusBarPortal
        )}

      {dayGroups.map((group) => (
        <MealInsightDaySection
          key={group.dateKey}
          group={group}
          registerSection={registerSection}
          onMealClick={onMealClick}
        />
      ))}

      <Sentinel ref={prefetchRef} aria-hidden />

      {loadingMore && reachedEnd && (
        <LoadingMore>
          <MealInsightSkeleton count={LOAD_MORE_SKELETON_COUNT} />
        </LoadingMore>
      )}

      {reachedCaughtUp && (
        <CaughtUp aria-hidden>
          <Translate msg="mealInsights.meals.allCaughtUp" />
        </CaughtUp>
      )}
    </List>
  );
};

export default MealInsightDayList;
